import { gql, useLazyQuery } from '@apollo/client'

import agentClient from '@/lib/apollo/client/agent'
import type { Author } from '@/lib/apollo/model/author'
import type { WrappedUseLazyQuery } from '@/lib/apollo/types'
import type { FraudDecision } from '@/types/Activity'
import type { AdverseCredit } from '@/types/adverseCredit'

const getApplicantQuery = gql`
  query ($customerId: String!, $subjectId: String!) {
    getApplicant(customerId: $customerId, subjectId: $subjectId) {
      id
      applicationId
      customerId
      type
      firstName
      middleName
      lastName
      fullName
      email
      dateOfBirth
      addressApplyingFor {
        nameOrNumber
        line1
        line2
        line3
        city
        postcode
        county
        country
      }
      agencyName
      tenancyStartDate
      swimLaneGroup
      referenceGroup
      fraudStatus
      fraudReason
      fraudDecision
      escalation {
        reason
        author {
          userId
          fullName
        }
      }
      avatar
      referenceTier
      joinedQueueAt
      joinedReopenQueueAt
      documents {
        id
        type
        fileName
        thumbnail
        createdAt
      }
      idEvidenceDocuments {
        id
        type
        fileName
        thumbnail
      }
      idType
      r2RShareCode
      companyNamedOfficer
      letterOfAuthorityProvided
      facialMatch
      adverseCredit {
        id
        caseNumber
        type
        state
        declaration
        amount
        courtDate
      }
      identityConfirmed
      residencyConfirmed
      processedBy {
        userId
        fullName
      }
      lastProcessedBy {
        userId
        fullName
      }
      lastProcessedAt
      supportContacts {
        id
        createdAt
        contactType
        status
        link
      }
      sanctions {
        status
        verifiedAt
      }
    }
  }
`

export type QueryVars = {
  customerId: string
  subjectId: string
}

export enum ApplicantType {
  tenant = 'tenant',
  guarantor = 'guarantor',
}

export enum ReferenceTier {
  essential = 'essential',
  advanced = 'advanced',
  advancedPlus = 'advancedPlus',
  priority = 'priority',
  hybrid = 'hybrid',
  pro = 'pro',
  corporate = 'corporate',
  dataCollection = 'dataCollection',
  selfServe = 'selfServe',
  selfServeStudent = 'selfServeStudent',
}

export type Address = {
  nameOrNumber: string | null
  line1: string | null
  line2: string | null
  line3: string | null
  city: string | null
  postcode: string | null
  county: string | null
  country: string | null
}

export type Escalation = {
  reason: string
  author: Author
}

export enum FraudStatus {
  notFraudulent = 'NotFlagged',
  awaitingFraudTeamReview = 'AwaitingFraudTeamReview',
  potential = 'FlaggedAsPotentiallyFraudulent',
  confirmed = 'ConfirmedFraudulent',
}

export type Document = {
  id: string
  type: string
  fileName: string
  thumbnail: string
  createdAt: string
}

export type SupportContactType = 'Webform' | 'Email' | 'NRLA Webform'

export type SupportContact = {
  id: string
  createdAt: string
  contactType: SupportContactType
  status: string
  link: string
}

export enum SanctionsStatus {
  ACTION_REQUIRED = 'ActionRequired',
  MATCH = 'Match',
  MISMATCH = 'Mismatch',
  NOT_FOUND = 'NotFound',
}

export type Sanctions = {
  status: SanctionsStatus
  verifiedAt: string | null
}

export type Applicant = {
  id: string
  applicationId: string
  customerId: string
  type: ApplicantType
  firstName: string
  middleName: string | null
  lastName: string
  fullName: string
  email: string
  dateOfBirth: string | null
  addressApplyingFor: Address
  agencyName: string
  tenancyStartDate: string
  swimLaneGroup: string
  referenceGroup: string
  fraudStatus: FraudStatus
  fraudReason: string | null
  fraudDecision: FraudDecision | null
  escalation: Escalation | null
  avatar: string | null
  referenceTier: ReferenceTier
  joinedQueueAt: string | null
  joinedReopenQueueAt: string | null
  documents: Document[]
  idEvidenceDocuments: Document[]
  idType: string
  r2RShareCode?: string
  companyNamedOfficer: boolean | null
  letterOfAuthorityProvided: boolean | null
  adverseCredit: AdverseCredit[] | null
  identityConfirmed: boolean
  residencyConfirmed: boolean
  processedBy: Author[]
  lastProcessedBy: Author | null
  lastProcessedAt: string | null
  supportContacts: SupportContact[]
  facialMatch: boolean | null
  sanctions: Sanctions | null
}

export type GetApplicantData = {
  getApplicant: Applicant
}

export const useGetApplicantQuery: WrappedUseLazyQuery<
  GetApplicantData,
  QueryVars
> = options =>
  useLazyQuery<GetApplicantData, QueryVars>(getApplicantQuery, {
    ...options,
    client: agentClient,
  })
